import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { labAPI } from '../../services/api';
import { useNotify } from '../../context/NotifyContext';
import { LabDemoThemedTip, LabDemoTippedExpand } from './LabDemographicsUi';

function triggerBlobDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/** Normalize axios errors so UI can always show actionable messages */
async function parseAxiosError(err) {
  const data = err.response?.data;
  const status = err.response?.status;
  if (!data) {
    return { message: err.message || 'Request failed', status, batchId: null };
  }
  if (typeof data === 'string') {
    return { message: data, status, batchId: null };
  }
  if (data instanceof Blob) {
    const text = await data.text();
    try {
      const j = JSON.parse(text);
      return { message: j.message || j.error || text, status, batchId: j.batchId || null };
    } catch {
      return { message: text.slice(0, 300), status, batchId: null };
    }
  }
  return {
    message: data.message || data.error || 'Request failed',
    status,
    batchId: data.batchId || null
  };
}

function ymNow() {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}`;
}

function ymdNow() {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`;
}

function monthBounds(ym) {
  if (!/^\d{4}-\d{2}$/.test(String(ym || ''))) return { from: '', to: '' };
  const [yy, mm] = String(ym).split('-').map(Number);
  const from = `${yy}-${String(mm).padStart(2, '0')}-01`;
  const toDate = new Date(yy, mm, 0);
  const to = `${yy}-${String(mm).padStart(2, '0')}-${String(toDate.getDate()).padStart(2, '0')}`;
  return { from, to };
}

function monthLabel(ym) {
  if (!/^\d{4}-\d{2}$/.test(String(ym || ''))) return 'All months';
  const [y, m] = String(ym).split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 1, 1));
  return d.toLocaleDateString(undefined, { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

function batchDateValue(batch) {
  return batch?.fileDate || batch?.reportDateEnd || batch?.createdAt || null;
}

function fmtBatchDate(batch) {
  const v = batchDateValue(batch);
  if (!v) return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

function fmtBatchRange(batch) {
  const from = batch?.reportDateStart ? new Date(batch.reportDateStart) : null;
  const to = batch?.reportDateEnd ? new Date(batch.reportDateEnd) : null;
  const fromTxt = from && !Number.isNaN(from.getTime()) ? from.toISOString().slice(0, 10) : '';
  const toTxt = to && !Number.isNaN(to.getTime()) ? to.toISOString().slice(0, 10) : '';
  if (fromTxt && toTxt) return `${fromTxt} to ${toTxt}`;
  return toTxt || fromTxt || fmtBatchDate(batch);
}

/** Dates you chose at upload (From / To); shown so single-day uploads show the exact calendar day */
function fmtUserUploadPeriod(batch) {
  if (!batch) return '';
  const from = batch.reportDateStart ? new Date(batch.reportDateStart) : null;
  const to = batch.reportDateEnd ? new Date(batch.reportDateEnd) : null;
  const fd = batch.fileDate ? new Date(batch.fileDate) : null;
  const fromOk = from && !Number.isNaN(from.getTime());
  const toOk = to && !Number.isNaN(to.getTime());
  const fdOk = fd && !Number.isNaN(fd.getTime());
  const fmt = (d) =>
    d.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' });
  if (fromOk && toOk) {
    const sameDay =
      from.getFullYear() === to.getFullYear() &&
      from.getMonth() === to.getMonth() &&
      from.getDate() === to.getDate();
    if (sameDay) return `${fmt(to)} — single-day import`;
    return `${fmt(from)} – ${fmt(to)}`;
  }
  if (toOk) return fmt(to);
  if (fdOk) return fmt(fd);
  return '';
}

function dayLabelFromKey(key) {
  if (!key || !/^\d{4}-\d{2}-\d{2}$/.test(key)) return key || '—';
  const [y, mo, d] = key.split('-').map(Number);
  const dt = new Date(y, mo - 1, d);
  if (Number.isNaN(dt.getTime())) return key;
  return dt.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
}

function ymdKeyLocal(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function normLoose(s) {
  return (s || '').toLowerCase().replace(/\s+/g, ' ').trim();
}

/** Strip leading accession from LIS patient line for comparison */
function patientLineTail(row, patientLine) {
  let t = (patientLine || '').trim();
  const sid = String(row.specimenId || '').trim();
  if (sid && t.startsWith(sid)) t = t.slice(sid.length).replace(/^[\s,]+/, '').trim();
  return t;
}

/**
 * Hide patientLine when it only repeats accession + name already shown in First/Last
 * (e.g. "000010082 Gyulvanesyan, Liana" vs typed Liana / Gyulvanes).
 */
function shouldShowPatientLine(row, firstVal, lastVal) {
  const pl = (row.patientLine || '').trim();
  if (!pl) return false;
  const fn = normLoose(firstVal);
  const ln = normLoose(lastVal);
  const tail = normLoose(patientLineTail(row, pl));
  if (!tail) return false;
  if (!fn && !ln) return true;
  const lastToken = (lastVal || '').split(/[\s,]+/).filter(Boolean)[0]?.toLowerCase() || '';
  const firstToken = (firstVal || '').split(/[\s,]+/).filter(Boolean)[0]?.toLowerCase() || '';
  if (lastToken && firstToken && tail.includes(lastToken) && tail.includes(firstToken)) return false;
  const commaForm = `${ln}, ${fn}`;
  const spaceForm = `${fn} ${ln}`;
  if (tail === commaForm || tail === spaceForm || tail === `${ln} ${fn}`) return false;
  return true;
}

function normTs(s) {
  return (s || '').trim().replace(/\s+/g, ' ');
}

function drawReceivedLines(drawDateTime, receivedDateTime) {
  const d = normTs(drawDateTime);
  const r = normTs(receivedDateTime);
  if (!d && !r) return [];
  if (d && r && d === r) return [d];
  if (d && !r) return [d];
  if (!d && r) return [r];
  return [d, r];
}

/** Only show raw LIS doctor when it differs from Provider field */
function shouldShowLisDoctorHint(exportDoctorDraft, doctorName) {
  const a = normLoose(exportDoctorDraft || '');
  const b = normLoose(doctorName || '');
  if (!b) return false;
  if (!a) return true;
  if (a === b) return false;
  if (a.length >= 4 && b.length >= 4 && (a.includes(b) || b.includes(a))) return false;
  return true;
}

function drawTimesHoverText(timeLines) {
  if (!timeLines?.length) return '';
  return timeLines.join('\n');
}

function rowDayKey(row) {
  const source = row?.drawAt || row?.receivedAt || row?.batch?.reportDateEnd || row?.batch?.fileDate || row?.updatedAt || row?.createdAt;
  if (!source) return 'unknown';
  const d = new Date(source);
  if (Number.isNaN(d.getTime())) return 'unknown';
  return ymdKeyLocal(d);
}

function rowDayLabel(row) {
  const key = rowDayKey(row);
  return key === 'unknown' ? 'Unknown day' : dayLabelFromKey(key);
}

/** Defaults when the API is unavailable; admin can override via Platform settings */
const DEFAULT_DEMO_UI = {
  sendOut: ['', 'Send out', 'No send out'],
  resultStatus: ['', 'Sent', 'Pending', 'Hold', 'Completed'],
  billingStatus: ['', 'Billed', 'Unbilled', 'Pending']
};

/** Include current cell value so legacy/imported text still appears until the user picks a listed option */
function mergeSelectOptions(options, currentValue) {
  const base = Array.isArray(options) && options.length ? options : [];
  const v = currentValue === null || currentValue === undefined ? '' : String(currentValue);
  if (v !== '' && !base.includes(v)) return [...base, v];
  return base;
}

/** Normalize populated clinic ref, plain ObjectId, or id string for <select value> */
function clinicRefToSelectValue(ref) {
  if (ref == null || ref === '') return '';
  if (typeof ref === 'object' && ref !== null && ref._id != null) return String(ref._id);
  return String(ref);
}

const LabDemographicsSheet = ({ clinics = [] }) => {
  const { showSuccess, showError, showInfo } = useNotify();
  const [batches, setBatches] = useState([]);
  const [demoRows, setDemoRows] = useState([]);
  const [demoTotal, setDemoTotal] = useState(0);
  const [busy, setBusy] = useState(false);
  const [demoEdits, setDemoEdits] = useState({});
  const [savingRows, setSavingRows] = useState({});
  const [saveErrorMsg, setSaveErrorMsg] = useState('');
  const [filterMonth, setFilterMonth] = useState(ymNow);
  const [autoMonthInitialized, setAutoMonthInitialized] = useState(false);
  const [selectedBatchId, setSelectedBatchId] = useState('');
  const [demoClinicFilter, setDemoClinicFilter] = useState('');
  const [filterDay, setFilterDay] = useState('');
  const [rangeFrom, setRangeFrom] = useState('');
  const [rangeTo, setRangeTo] = useState('');
  /** Server caps at 500 — maximize rows per page for dense sheet view */
  const limit = 500;
  const [uploadFromDate, setUploadFromDate] = useState('');
  const [uploadToDate, setUploadToDate] = useState('');
  const [uploadSingleDay, setUploadSingleDay] = useState('');
  const [uploadMode, setUploadMode] = useState('range');
  const [uploadDatesTouched, setUploadDatesTouched] = useState(false);
  const [showUploadPopup, setShowUploadPopup] = useState(false);
  const [showExportPopup, setShowExportPopup] = useState(false);
  const [showRowDatesPopup, setShowRowDatesPopup] = useState(false);
  const [scrollDayLabel, setScrollDayLabel] = useState('');
  const [activeDayIndex, setActiveDayIndex] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showScrollTopBtn, setShowScrollTopBtn] = useState(false);
  const [showScrollBottomBtn, setShowScrollBottomBtn] = useState(false);
  const rootRef = useRef(null);
  const autoSaveTimersRef = useRef({});
  const [demoUiOptions, setDemoUiOptions] = useState(DEFAULT_DEMO_UI);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await labAPI.getDemographicsUiOptions();
        if (cancelled) return;
        setDemoUiOptions({
          sendOut: Array.isArray(data.sendOut) && data.sendOut.length ? data.sendOut : DEFAULT_DEMO_UI.sendOut,
          resultStatus:
            Array.isArray(data.resultStatus) && data.resultStatus.length
              ? data.resultStatus
              : DEFAULT_DEMO_UI.resultStatus,
          billingStatus:
            Array.isArray(data.billingStatus) && data.billingStatus.length
              ? data.billingStatus
              : DEFAULT_DEMO_UI.billingStatus
        });
      } catch (e) {
        console.error(e);
        if (!cancelled) setDemoUiOptions(DEFAULT_DEMO_UI);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      setShowUploadPopup(false);
      setShowExportPopup(false);
      setShowRowDatesPopup(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const loadBatches = useCallback(async () => {
    try {
      const { data } = await labAPI.getDemographicsBatches();
      setBatches(data.batches || []);
    } catch (e) {
      console.error(e);
      const details = await parseAxiosError(e);
      showError(`Could not load import files: ${details.message}`);
    }
  }, [showError]);

  const loadDemoRows = useCallback(async () => {
    setBusy(true);
    try {
      const params = {
        limit,
        clinicId: demoClinicFilter || undefined
      };
      if (selectedBatchId) params.batchId = selectedBatchId;
      if (filterMonth) params.month = filterMonth;
      if (filterDay) params.day = filterDay;
      if (rangeFrom) params.dateFrom = rangeFrom;
      if (rangeTo) params.dateTo = rangeTo;

      const { data } = await labAPI.getDemographicsRows(params);
      setDemoRows(data.rows || []);
      setDemoTotal(data.total ?? 0);
    } catch (e) {
      console.error(e);
      const details = await parseAxiosError(e);
      showError(`Could not load demographics rows: ${details.message}`);
    } finally {
      setBusy(false);
    }
  }, [
    limit,
    demoClinicFilter,
    selectedBatchId,
    filterMonth,
    filterDay,
    rangeFrom,
    rangeTo,
    showError
  ]);

  useEffect(() => {
    loadBatches();
  }, [loadBatches]);

  useEffect(() => {
    loadDemoRows();
  }, [loadDemoRows]);

  const batchesInMonth = useMemo(() => {
    if (!filterMonth) return batches;
    const [y, m] = filterMonth.split('-').map(Number);
    return batches.filter((b) => {
      const d = new Date(batchDateValue(b));
      return d.getFullYear() === y && d.getMonth() + 1 === m;
    });
  }, [batches, filterMonth]);

  useEffect(() => {
    if (autoMonthInitialized) return;
    if (!batches.length) {
      setAutoMonthInitialized(true);
      return;
    }
    const latest = [...batches]
      .sort((a, b) => {
        const da = new Date(batchDateValue(a) || 0).getTime();
        const db = new Date(batchDateValue(b) || 0).getTime();
        return db - da;
      })
      .find(Boolean);
    const latestDate = latest ? new Date(batchDateValue(latest)) : null;
    if (latestDate && !Number.isNaN(latestDate.getTime())) {
      const latestYm = `${latestDate.getFullYear()}-${String(latestDate.getMonth() + 1).padStart(2, '0')}`;
      setFilterMonth(latestYm);
    }
    setAutoMonthInitialized(true);
  }, [autoMonthInitialized, batches]);

  useEffect(() => {
    if (uploadDatesTouched) return;
    const { from, to } = monthBounds(filterMonth);
    setUploadFromDate(from);
    setUploadToDate(to);
    setUploadSingleDay(to);
  }, [filterMonth, uploadDatesTouched]);

  useEffect(() => {
    if (selectedBatchId && !batchesInMonth.some((b) => b._id === selectedBatchId)) {
      setSelectedBatchId('');
    }
  }, [batchesInMonth, selectedBatchId]);

  useEffect(() => {
    if (busy) return;
    if (selectedBatchId) return;
    if (!batchesInMonth.length) return;
    if (demoTotal > 0) return;
    // Fallback: if the month view is empty but imports exist for this month, open the latest file.
    const latest = [...batchesInMonth].sort((a, b) => {
      const da = new Date(batchDateValue(a) || 0).getTime();
      const db = new Date(batchDateValue(b) || 0).getTime();
      return db - da;
    })[0];
    if (latest) setSelectedBatchId(latest._id);
  }, [busy, selectedBatchId, batchesInMonth, demoTotal]);

  const selectedBatch = useMemo(
    () => (selectedBatchId ? batchesInMonth.find((b) => b._id === selectedBatchId) : null),
    [batchesInMonth, selectedBatchId]
  );

  /** Export API requires one batch; if the table shows “all month” but only one import exists, use it */
  const exportBatchId = useMemo(() => {
    if (selectedBatchId) return selectedBatchId;
    if (batchesInMonth.length === 1) return batchesInMonth[0]._id;
    return '';
  }, [selectedBatchId, batchesInMonth]);

  const importSelectTitle = selectedBatch
    ? `${selectedBatch.originalFilename || 'File'} • ${fmtUserUploadPeriod(selectedBatch) || fmtBatchRange(selectedBatch) || 'no date'} (${selectedBatch.rowCount ?? 0} rows)`
    : 'All files for the selected month';


  const demoCell = (row, field) =>
    demoEdits[row._id]?.[field] !== undefined ? demoEdits[row._id][field] : row[field] ?? '';

  const saveDemoRow = useCallback(async (row, explicitPatch) => {
    const patch = explicitPatch || demoEdits[row._id];
    if (!patch || !Object.keys(patch).length) return;
    setSavingRows((s) => ({ ...s, [row._id]: true }));
    setSaveErrorMsg('');
    try {
      await labAPI.patchDemographicsRow(row._id, patch);
      setDemoEdits((e) => {
        const n = { ...e };
        delete n[row._id];
        return n;
      });
      setDemoRows((rows) => rows.map((r) => (r._id === row._id ? { ...r, ...patch } : r)));
    } catch (err) {
      const details = await parseAxiosError(err);
      setSaveErrorMsg(details.message || 'Save failed');
      showError(`Auto-save failed: ${details.message}`);
    } finally {
      setSavingRows((s) => {
        const n = { ...s };
        delete n[row._id];
        return n;
      });
    }
  }, [demoEdits, showError]);

  const scheduleAutoSave = useCallback(
    (rowId, rowSnapshot, patchSnapshot) => {
      if (autoSaveTimersRef.current[rowId]) {
        window.clearTimeout(autoSaveTimersRef.current[rowId]);
      }
      autoSaveTimersRef.current[rowId] = window.setTimeout(async () => {
        await saveDemoRow(rowSnapshot, patchSnapshot);
        delete autoSaveTimersRef.current[rowId];
      }, 900);
    },
    [saveDemoRow]
  );

  const setDemoCell = (row, field, val) => {
    const nextPatch = { ...(demoEdits[row._id] || {}), [field]: val };
    setDemoEdits((e) => ({ ...e, [row._id]: nextPatch }));
    scheduleAutoSave(row._id, row, nextPatch);
  };

  const saveAllNow = async () => {
    const entries = Object.entries(demoEdits);
    if (!entries.length) return;
    entries.forEach(([rowId]) => {
      if (autoSaveTimersRef.current[rowId]) {
        window.clearTimeout(autoSaveTimersRef.current[rowId]);
        delete autoSaveTimersRef.current[rowId];
      }
    });
    const rowsById = new Map(demoRows.map((r) => [r._id, r]));
    for (const [rowId, patch] of entries) {
      const row = rowsById.get(rowId);
      if (!row) continue;
      // eslint-disable-next-line no-await-in-loop
      await saveDemoRow(row, patch);
    }
    if (!saveErrorMsg) showSuccess('All changes saved.');
  };

  const onUploadPdf = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const effectiveFrom = uploadMode === 'single' ? uploadSingleDay : uploadFromDate;
    const effectiveTo = uploadMode === 'single' ? uploadSingleDay : uploadToDate;
    if (!effectiveFrom || !effectiveTo) {
      showInfo(uploadMode === 'single' ? 'Select the day before uploading demographics.' : 'Select both From date and To date before uploading demographics.');
      e.target.value = '';
      return;
    }
    if (effectiveFrom > effectiveTo) {
      showInfo('From date must be on or before To date.');
      e.target.value = '';
      return;
    }
    const existingForRange = batches.filter((b) => {
      const from = b?.reportDateStart ? new Date(b.reportDateStart).toISOString().slice(0, 10) : '';
      const to = b?.reportDateEnd ? new Date(b.reportDateEnd).toISOString().slice(0, 10) : '';
      return from === effectiveFrom && to === effectiveTo;
    }).length;
    let overwriteDateRange = false;
    const scopeLabel = effectiveFrom === effectiveTo ? effectiveFrom : `${effectiveFrom} to ${effectiveTo}`;
    if (existingForRange > 0) {
      const ok = window.confirm(
        `This day/range already has ${existingForRange} document(s): ${effectiveFrom} to ${effectiveTo}. Click OK to overwrite that day/range data.`
      );
      if (!ok) {
        e.target.value = '';
        return;
      }
      overwriteDateRange = true;
    }
    const fd = new FormData();
    fd.append('pdf', file);
    fd.append('fileDateFrom', effectiveFrom);
    fd.append('fileDateTo', effectiveTo);
    if (overwriteDateRange) fd.append('overwriteDateRange', 'true');
    setBusy(true);
    try {
      const { data } = await labAPI.postDemographicsPdf(fd);
      const action = overwriteDateRange ? 'overwrote' : 'imported';
      showSuccess(`Upload successful: "${file.name}" ${action} ${data.rowCount ?? 0} row(s) for ${scopeLabel}.`);
      if (Array.isArray(data.warnings)) {
        data.warnings.filter(Boolean).forEach((msg) => showInfo(msg));
      }
      await loadBatches();
      if (data.batch?._id) {
        setSelectedBatchId(data.batch._id);
        setFilterMonth(effectiveTo.slice(0, 7));
      }
    } catch (err) {
      const details = await parseAxiosError(err);
      if (details.status === 409) {
        showError(`Upload blocked for ${scopeLabel}: ${details.message}`);
      } else if (details.status === 422) {
        showError(`Upload failed: "${file.name}" has no readable demographics rows. Please verify PDF format and try again.`);
      } else if (details.status === 400) {
        showError(`Upload failed: could not read "${file.name}". Please upload a valid demographics PDF.`);
      } else {
        showError(`Upload failed for "${file.name}" (${scopeLabel}): ${details.message}`);
      }
    } finally {
      setBusy(false);
      e.target.value = '';
    }
  };

  const onDeleteBatch = async () => {
    if (!selectedBatchId) {
      showInfo('Select an import file first, then click Delete import.');
      return;
    }
    const batch = batches.find((b) => String(b._id) === String(selectedBatchId));
    const label = batch?.originalFilename || selectedBatchId;
    const ok = window.confirm(`Delete "${label}" and all its imported rows? This cannot be undone.`);
    if (!ok) return;
    setBusy(true);
    try {
      await labAPI.deleteDemographicsBatch(selectedBatchId);
      showSuccess('Import file deleted.');
      setSelectedBatchId('');
      setDemoEdits({});
      await loadBatches();
      await loadDemoRows();
    } catch (err) {
      const details = await parseAxiosError(err);
      showError(`Delete failed: ${details.message}`);
    } finally {
      setBusy(false);
    }
  };

  const onExportExcel = async () => {
    if (!exportBatchId) {
      showInfo(
        batchesInMonth.length > 1
          ? 'Choose one import file in the dropdown, then download Excel for that file.'
          : 'No import file is available for this month yet.'
      );
      return;
    }
    setBusy(true);
    try {
      const res = await labAPI.exportDemographicsExcel({
        batchId: exportBatchId,
        clinicId: demoClinicFilter || undefined
      });
      triggerBlobDownload(res.data, `demographics-${exportBatchId}.xlsx`);
      showSuccess('Excel download started.');
    } catch (err) {
      const details = await parseAxiosError(err);
      showError(`Export failed: ${details.message}`);
    } finally {
      setBusy(false);
    }
  };

  const clearDateFilters = () => {
    setFilterDay('');
    setRangeFrom('');
    setRangeTo('');
  };

  useEffect(() => {
    setFilterDay('');
    setRangeFrom('');
    setRangeTo('');
  }, []);

  useEffect(
    () => () => {
      Object.values(autoSaveTimersRef.current).forEach((tid) => window.clearTimeout(tid));
    },
    []
  );

  useEffect(() => {
    const onFs = () => setIsFullscreen(document.fullscreenElement === rootRef.current);
    document.addEventListener('fullscreenchange', onFs);
    return () => document.removeEventListener('fullscreenchange', onFs);
  }, []);

  const toggleFullscreen = useCallback(async () => {
    try {
      if (document.fullscreenElement === rootRef.current) {
        await document.exitFullscreen();
      } else {
        await rootRef.current?.requestFullscreen?.();
      }
    } catch {
      // ignore fullscreen API failures
    }
  }, []);

  const dayHeaders = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const row of demoRows) {
      const key = rowDayKey(row);
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({ key, label: rowDayLabel(row) });
    }
    return out;
  }, [demoRows]);

  useEffect(() => {
    const first = dayHeaders[0]?.label || '';
    setActiveDayIndex(0);
    setScrollDayLabel(first);
  }, [dayHeaders]);

  const jumpToDay = useCallback(
    (idx) => {
      if (!dayHeaders.length) return;
      const nextIdx = Math.max(0, Math.min(dayHeaders.length - 1, idx));
      const target = dayHeaders[nextIdx];
      setActiveDayIndex(nextIdx);
      setScrollDayLabel(target.label);
      const el = document.querySelector(`tr[data-day-key="${target.key}"]`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },
    [dayHeaders]
  );

  useEffect(() => {
    let rafId = 0;
    const updateOnScreenDay = () => {
      const root = rootRef.current;
      if (!root) return;
      const headers = Array.from(root.querySelectorAll('tr[data-day-header="1"]'));
      if (!headers.length) return;
      let activeLabel = headers[0].getAttribute('data-day-label') || '';
      for (const h of headers) {
        const r = h.getBoundingClientRect();
        if (r.top <= 170) activeLabel = h.getAttribute('data-day-label') || activeLabel;
        else break;
      }
      if (!activeLabel) return;
      setScrollDayLabel(activeLabel);
      const idx = dayHeaders.findIndex((d) => d.label === activeLabel);
      if (idx >= 0) setActiveDayIndex(idx);
    };
    const onScroll = () => {
      window.cancelAnimationFrame(rafId);
      rafId = window.requestAnimationFrame(updateOnScreenDay);
    };
    updateOnScreenDay();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.cancelAnimationFrame(rafId);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [dayHeaders]);
  const scrollPageToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const scrollPageToBottom = useCallback(() => {
    const max = Math.max(
      document.body.scrollHeight,
      document.documentElement.scrollHeight,
      document.body.offsetHeight,
      document.documentElement.offsetHeight
    );
    window.scrollTo({ top: max, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    const updateScrollButtons = () => {
      const scrollTop = window.scrollY || window.pageYOffset || 0;
      const doc = document.documentElement;
      const body = document.body;
      const scrollHeight = Math.max(doc.scrollHeight, body.scrollHeight, doc.offsetHeight, body.offsetHeight);
      const viewport = window.innerHeight || doc.clientHeight || 0;
      const maxScroll = Math.max(0, scrollHeight - viewport);
      setShowScrollTopBtn(scrollTop > 20);
      setShowScrollBottomBtn(scrollTop < maxScroll - 20);
    };
    updateScrollButtons();
    window.addEventListener('scroll', updateScrollButtons, { passive: true });
    window.addEventListener('resize', updateScrollButtons);
    return () => {
      window.removeEventListener('scroll', updateScrollButtons);
      window.removeEventListener('resize', updateScrollButtons);
    };
  }, []);

  const monthDateDefaults = useMemo(() => monthBounds(filterMonth), [filterMonth]);
  const linkedRowsOnPage = useMemo(
    () => demoRows.filter((row) => row.matchedClinic?._id || row.matchedClinic).length,
    [demoRows]
  );
  const unlinkedRowsOnPage = Math.max(0, demoRows.length - linkedRowsOnPage);
  const unsavedCount = useMemo(() => Object.keys(demoEdits).length, [demoEdits]);
  const savingCount = useMemo(() => Object.keys(savingRows).length, [savingRows]);
  const saveIndicatorText = savingCount
    ? `Saving ${savingCount} row(s)...`
    : unsavedCount
      ? `${unsavedCount} row(s) pending autosave`
      : saveErrorMsg
        ? `Save issue: ${saveErrorMsg}`
        : 'All changes saved';
  const scrollNavButtons = (
    <div className="lab-demo-float-nav-layer" aria-hidden="false">
      <div className="lab-demo-day-nav">
        <button
          type="button"
          className={`btn btn-outline lab-demo-day-nav-arrow${showScrollTopBtn ? '' : ' is-inactive'}`}
          onClick={() => {
            if (showScrollTopBtn) scrollPageToTop();
          }}
          title="Go to top"
          aria-label="Go to top"
        >
          ↑
        </button>
        <button
          type="button"
          className="btn btn-outline"
          style={{ padding: '2px 8px', fontSize: '0.72rem' }}
          onClick={() => jumpToDay(0)}
          disabled={activeDayIndex <= 0}
        >
          First day
        </button>
        <button
          type="button"
          className="btn btn-outline"
          style={{ padding: '2px 8px', fontSize: '0.72rem' }}
          onClick={() => jumpToDay(activeDayIndex - 1)}
          disabled={activeDayIndex <= 0}
        >
          Previous day
        </button>
        <span style={{ whiteSpace: 'nowrap' }}>
          On screen day: <strong>{scrollDayLabel || '—'}</strong>
        </span>
        <button
          type="button"
          className="btn btn-outline"
          style={{ padding: '2px 8px', fontSize: '0.72rem' }}
          onClick={() => jumpToDay(activeDayIndex + 1)}
          disabled={activeDayIndex >= dayHeaders.length - 1}
        >
          Next day
        </button>
        <button
          type="button"
          className="btn btn-outline"
          style={{ padding: '2px 8px', fontSize: '0.72rem' }}
          onClick={() => jumpToDay(dayHeaders.length - 1)}
          disabled={activeDayIndex >= dayHeaders.length - 1}
        >
          Last day
        </button>
      </div>
      <button
        type="button"
        className={`btn btn-outline lab-demo-float-nav lab-demo-float-nav--bottom${showScrollBottomBtn ? '' : ' is-inactive'}`}
        onClick={() => {
          if (showScrollBottomBtn) scrollPageToBottom();
        }}
        title="Go to bottom"
        aria-label="Go to bottom"
      >
        ↓
      </button>
    </div>
  );
  return (
    <div
      ref={rootRef}
      className="card lab-demographics-sheet"
      style={{
        marginTop: '0',
        marginBottom: '0',
        paddingTop: '8px',
        paddingBottom: '8px',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <h2 className="card-title lab-demographics-sheet__title">Demographics</h2>
      <p className="lab-demographics-sheet__intro">
        Google-Sheets style view: all rows for the selected month load automatically.
      </p>
      <p style={{ fontSize: '0.82rem', marginTop: '-4px', marginBottom: '6px', color: 'var(--text-secondary)' }}>
        Working month: <strong style={{ color: 'var(--text-primary)' }}>{monthLabel(filterMonth)}</strong>
      </p>
      <div className="lab-demo-filters" role="region" aria-label="Demographics controls" style={{ marginBottom: '6px' }}>
        <div className="lab-demo-actions-bar" style={{ justifyContent: 'space-between', alignItems: 'center', gap: '10px' }}>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
            <strong>Row dates</strong>: optional day/range filter. <strong>Upload</strong>: add/overwrite a selected day or range. <strong>Export</strong>: month/file export.
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button type="button" className="btn btn-outline" onClick={saveAllNow} disabled={!unsavedCount && !savingCount}>
              Save all now
            </button>
            <button type="button" className="btn btn-outline" onClick={toggleFullscreen}>
              {isFullscreen ? 'Exit full screen' : 'Full screen'}
            </button>
            <button type="button" className="btn btn-outline" onClick={() => setShowRowDatesPopup(true)}>
              Row dates{filterDay || rangeFrom || rangeTo ? ' (active)' : ''}
            </button>
            <button type="button" className="btn btn-outline" onClick={() => setShowUploadPopup(true)}>
              Upload
            </button>
            <button type="button" className="btn btn-outline" onClick={() => setShowExportPopup(true)}>
              Export
            </button>
          </div>
        </div>
      </div>

      {selectedBatch && (
        <p style={{ fontSize: '0.75rem', margin: '0 0 10px', color: 'var(--text-secondary)' }}>
          File scope: <strong>{selectedBatch.originalFilename || 'File'}</strong> ({fmtUserUploadPeriod(selectedBatch) || fmtBatchRange(selectedBatch) || '—'}).
        </p>
      )}

      {showRowDatesPopup &&
        createPortal(
          <div className="lab-demo-popup-backdrop" onMouseDown={() => setShowRowDatesPopup(false)}>
            <div className="lab-demo-popup-card" onMouseDown={(e) => e.stopPropagation()}>
              <div className="lab-demo-popup-head">
                <h3 className="lab-demo-popup-title">Row dates</h3>
                <button type="button" className="btn btn-outline" onClick={() => setShowRowDatesPopup(false)}>
                  Close
                </button>
              </div>
              <p className="lab-demo-filters__hint">Filter rows by draw/received dates. This does not change imported files.</p>
              <div className="lab-demo-filters__grid lab-demo-filters__grid--dates">
                <div className="form-group lab-demo-filters__field" style={{ margin: 0 }}>
                  <label className="form-label" htmlFor="lab-demo-day">Single day</label>
                  <input
                    id="lab-demo-day"
                    type="date"
                    className="form-input"
                    value={filterDay}
                    onChange={(e) => {
                      setFilterDay(e.target.value);
                      if (e.target.value) {
                        setRangeFrom('');
                        setRangeTo('');
                      }
                    }}
                  />
                </div>
                <div className="form-group lab-demo-filters__field" style={{ margin: 0 }}>
                  <label className="form-label" htmlFor="lab-demo-from">From</label>
                  <input
                    id="lab-demo-from"
                    type="date"
                    className="form-input"
                    value={rangeFrom}
                    onChange={(e) => {
                      setRangeFrom(e.target.value);
                      if (e.target.value) setFilterDay('');
                    }}
                  />
                </div>
                <div className="form-group lab-demo-filters__field" style={{ margin: 0 }}>
                  <label className="form-label" htmlFor="lab-demo-to">To</label>
                  <input
                    id="lab-demo-to"
                    type="date"
                    className="form-input"
                    value={rangeTo}
                    onChange={(e) => {
                      setRangeTo(e.target.value);
                      if (e.target.value) setFilterDay('');
                    }}
                  />
                </div>
                <button type="button" className="btn btn-outline lab-demo-filters__clear" onClick={clearDateFilters}>
                  Clear dates
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {showUploadPopup &&
        createPortal(
          <div className="lab-demo-popup-backdrop" onMouseDown={() => setShowUploadPopup(false)}>
            <div className="lab-demo-popup-card" onMouseDown={(e) => e.stopPropagation()}>
              <div className="lab-demo-popup-head">
                <h3 className="lab-demo-popup-title">Upload</h3>
                <button type="button" className="btn btn-outline" onClick={() => setShowUploadPopup(false)}>
                  Close
                </button>
              </div>
              <p className="lab-demo-filters__hint">Choose single day or date range, then upload. If that day/range already has a document, you will be warned before overwrite.</p>
              <div className="lab-demo-filters__grid lab-demo-filters__grid--primary">
                <div className="form-group lab-demo-filters__field" style={{ margin: 0, gridColumn: '1 / -1' }}>
                  <label className="form-label">Upload mode</label>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      className={uploadMode === 'single' ? 'btn btn-primary' : 'btn btn-outline'}
                      onClick={() => setUploadMode('single')}
                      title="Upload one specific day"
                    >
                      Single day
                    </button>
                    <button
                      type="button"
                      className={uploadMode === 'range' ? 'btn btn-primary' : 'btn btn-outline'}
                      onClick={() => setUploadMode('range')}
                      title="Upload a from/to date range"
                    >
                      Date range
                    </button>
                  </div>
                </div>
                {uploadMode === 'single' ? (
                  <div className="form-group lab-demo-filters__field" style={{ margin: 0 }}>
                    <label className="form-label" htmlFor="lab-demo-single-day">Day (required)</label>
                    <input
                      id="lab-demo-single-day"
                      type="date"
                      className="form-input"
                      value={uploadSingleDay}
                      onChange={(e) => setUploadSingleDay(e.target.value)}
                      max={ymdNow()}
                    />
                  </div>
                ) : (
                  <>
                    <div className="form-group lab-demo-filters__field" style={{ margin: 0 }}>
                      <label className="form-label" htmlFor="lab-demo-file-date-from">From date (required)</label>
                      <input
                        id="lab-demo-file-date-from"
                        type="date"
                        className="form-input"
                        value={uploadFromDate}
                        onChange={(e) => {
                          setUploadDatesTouched(true);
                          setUploadFromDate(e.target.value);
                        }}
                        max={ymdNow()}
                      />
                    </div>
                    <div className="form-group lab-demo-filters__field" style={{ margin: 0 }}>
                      <label className="form-label" htmlFor="lab-demo-file-date-to">To date (required)</label>
                      <input
                        id="lab-demo-file-date-to"
                        type="date"
                        className="form-input"
                        value={uploadToDate}
                        onChange={(e) => {
                          setUploadDatesTouched(true);
                          setUploadToDate(e.target.value);
                        }}
                        min={uploadFromDate || undefined}
                        max={ymdNow()}
                      />
                    </div>
                  </>
                )}
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={() => {
                    setUploadDatesTouched(false);
                    setUploadFromDate(monthDateDefaults.from);
                    setUploadToDate(monthDateDefaults.to);
                    setUploadSingleDay(monthDateDefaults.to);
                  }}
                  title="Use selected month dates"
                >
                  Use month dates
                </button>
                <label
                  className="btn btn-outline lab-demo-filters__upload"
                  style={{
                    cursor: busy ? 'wait' : 'pointer',
                    justifyContent: 'center',
                    opacity: 1
                  }}
                  title="Upload demographics PDF"
                >
                  {busy ? 'Working…' : 'Upload PDF'}
                  <input
                    type="file"
                    accept=".pdf"
                    hidden
                    disabled={busy}
                    onChange={onUploadPdf}
                  />
                </label>
              </div>
            </div>
          </div>,
          document.body
        )}

      {showExportPopup &&
        createPortal(
          <div className="lab-demo-popup-backdrop" onMouseDown={() => setShowExportPopup(false)}>
            <div className="lab-demo-popup-card" onMouseDown={(e) => e.stopPropagation()}>
              <div className="lab-demo-popup-head">
                <h3 className="lab-demo-popup-title">Export</h3>
                <button type="button" className="btn btn-outline" onClick={() => setShowExportPopup(false)}>
                  Close
                </button>
              </div>
              <div className="lab-demo-filters__grid lab-demo-filters__grid--primary">
                <div className="form-group lab-demo-filters__field" style={{ margin: 0 }}>
                  <label className="form-label" htmlFor="lab-demo-month">Month</label>
                  <input
                    id="lab-demo-month"
                    type="month"
                    className="form-input"
                    value={filterMonth}
                    onChange={(e) => {
                      const nextMonth = e.target.value;
                      setFilterMonth(nextMonth);
                      setSelectedBatchId('');
                      setFilterDay('');
                      setRangeFrom('');
                      setRangeTo('');
                    }}
                  />
                </div>
                <div className="form-group lab-demo-filters__field lab-demo-filters__import" style={{ margin: 0 }}>
                  <label className="form-label" htmlFor="lab-demo-import-file">Import file</label>
                  <select
                    id="lab-demo-import-file"
                    className="form-input"
                    value={selectedBatchId}
                    title={importSelectTitle}
                    onChange={(e) => setSelectedBatchId(e.target.value)}
                  >
                    <option value="">All files this month</option>
                    {batchesInMonth.map((b) => {
                      const uploadBit = fmtUserUploadPeriod(b) || fmtBatchRange(b) || 'no date';
                      const optLabel = `${b.originalFilename || 'Import'} • upload: ${uploadBit} • ${b.rowCount ?? 0} rows`;
                      return (
                        <option key={b._id} value={b._id} title={b.originalFilename || optLabel}>
                          {optLabel}
                        </option>
                      );
                    })}
                  </select>
                </div>
                <div className="form-group lab-demo-filters__field" style={{ margin: 0 }}>
                  <label className="form-label" htmlFor="lab-demo-clinic">Link clinic</label>
                  <select
                    id="lab-demo-clinic"
                    className="form-input"
                    value={demoClinicFilter}
                    onChange={(e) => setDemoClinicFilter(e.target.value)}
                  >
                    <option value="">All clinics</option>
                    {clinics.map((c) => (
                      <option key={c._id} value={c._id} title={c.clinicName || c.name}>
                        {c.clinicName || c.name}
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  type="button"
                  className="btn btn-primary lab-demo-filters__export"
                  disabled={busy || !exportBatchId}
                  onClick={onExportExcel}
                  title={
                    batchesInMonth.length > 1 && !selectedBatchId
                      ? 'Choose one import file, or narrow the month until only one file exists.'
                      : 'Download Excel for this import (optional link-clinic filter applies).'
                  }
                >
                  Download Excel
                </button>
                <button
                  type="button"
                  className="btn btn-outline"
                  disabled={busy || !selectedBatchId}
                  onClick={onDeleteBatch}
                  title="Delete selected import file and all rows imported from it."
                >
                  Delete import
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '4px' }}>
        <strong style={{ color: saveErrorMsg ? '#ef4444' : 'var(--logo-cyan-teal)' }}>{saveIndicatorText}</strong>
        {' · '}
        <strong>{demoRows.length}</strong> / <strong>{demoTotal}</strong> rows
        · showing up to {limit} rows · linked on screen: <strong style={{ color: 'var(--logo-cyan-teal)' }}>{linkedRowsOnPage}</strong> · unlinked on screen:{' '}
        <strong style={{ color: 'var(--text-primary)' }}>{unlinkedRowsOnPage}</strong> · scroll horizontally for all columns.
      </p>
      <div
        className="table-wrapper lab-demo-table-scroll"
        style={{
          overflowX: 'auto',
          overflowY: 'visible',
          scrollBehavior: 'smooth',
          position: 'relative'
        }}
      >
        <table className="table lab-demo-sheet-table lab-demo-sheet-table--dense" style={{ fontSize: '0.72rem' }}>
          <thead>
            <tr>
              <th style={{ minWidth: '8.25rem' }}>Accession</th>
              <th style={{ minWidth: '10.5rem' }}>Patient</th>
              <th style={{ width: '5.25rem' }}>DOB</th>
              <th style={{ minWidth: '7rem' }}>Provider</th>
              <th style={{ minWidth: '7rem' }}>Clinic (medical group)</th>
              <th style={{ minWidth: '5.5rem' }}>Panel</th>
              <th style={{ minWidth: '6.5rem' }}>Draw / recv</th>
              <th style={{ minWidth: '8rem' }}>Tests</th>
              <th style={{ minWidth: '7rem' }}>Notes</th>
              <th style={{ minWidth: '4rem' }}>Send out</th>
              <th style={{ minWidth: '4.5rem' }}>Result</th>
              <th style={{ minWidth: '4.5rem' }}>Payment</th>
              <th style={{ minWidth: '6.5rem' }}>Link clinic</th>
            </tr>
          </thead>
          <tbody>
            {demoRows.map((row, idx) => {
              const batch = row.batch;
              const currentDayKey = rowDayKey(row);
              const prevDayKey = idx > 0 ? rowDayKey(demoRows[idx - 1]) : null;
              const showDayHeader = idx === 0 || currentDayKey !== prevDayKey;
              const pf = demoCell(row, 'patientFirst');
              const pls = demoCell(row, 'patientLast');
              const showPatientLine = shouldShowPatientLine(row, pf, pls);
              const expDoc = demoCell(row, 'exportDoctor');
              const showLisDoc = shouldShowLisDoctorHint(expDoc, row.doctorName);
              const timeLines = drawReceivedLines(row.drawDateTime, row.receivedDateTime);
              const accessionTip = [demoCell(row, 'specimenId'), batch?.originalFilename && `Source file: ${batch.originalFilename}`]
                .filter(Boolean)
                .join('\n\n');
              const matchedId =
                demoEdits[row._id]?.matchedClinic !== undefined
                  ? clinicRefToSelectValue(demoEdits[row._id].matchedClinic)
                  : clinicRefToSelectValue(row.matchedClinic);
              const matchedLabel =
                clinics.find((c) => String(c._id) === String(matchedId))?.clinicName ||
                clinics.find((c) => String(c._id) === String(matchedId))?.name ||
                '';
              return (
                <React.Fragment key={row._id}>
                  {showDayHeader && (
                    <tr data-day-header="1" data-day-label={rowDayLabel(row)} data-day-key={currentDayKey}>
                      <td
                        colSpan={13}
                        style={{
                          fontSize: '0.79rem',
                          fontWeight: 800,
                          letterSpacing: '0.02em',
                          color: '#eafffb',
                          background: 'linear-gradient(90deg, color-mix(in srgb, var(--logo-cyan-teal) 36%, #0b1327), color-mix(in srgb, var(--logo-cyan-teal) 18%, #0b1327))',
                          borderTop: '1px solid color-mix(in srgb, var(--logo-cyan-teal) 62%, #1f2937)',
                          borderBottom: '1px solid color-mix(in srgb, var(--logo-cyan-teal) 48%, #1f2937)',
                          textShadow: '0 0 10px rgba(51,255,217,0.28)',
                          position: 'sticky',
                          top: 0,
                          zIndex: 1
                        }}
                      >
                        {rowDayLabel(row)}
                      </td>
                    </tr>
                  )}
                <tr>
                  <td style={{ verticalAlign: 'top' }}>
                    <LabDemoTippedExpand
                      tipText={accessionTip}
                      multiline={false}
                      className="form-input lab-demo-cell-input"
                      style={{
                        minWidth: '7.5rem',
                        width: '100%',
                        fontFamily: 'monospace',
                        fontSize: '0.72rem',
                        letterSpacing: '0.02em',
                        padding: '4px 6px'
                      }}
                      disabled={busy}
                      value={demoCell(row, 'specimenId')}
                      onChange={(e) => setDemoCell(row, 'specimenId', e.target.value)}
                    />
                  </td>
                  <td style={{ verticalAlign: 'top' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 0 }}>
                      <div style={{ display: 'flex', gap: '4px', flexWrap: 'nowrap', alignItems: 'stretch' }}>
                        <LabDemoTippedExpand
                          tipText={pf}
                          multiline={false}
                          className="form-input lab-demo-cell-input"
                          style={{ flex: '1 1 50%', minWidth: 0, fontSize: '0.7rem' }}
                          placeholder="First"
                          disabled={busy}
                          value={pf}
                          onChange={(e) => setDemoCell(row, 'patientFirst', e.target.value)}
                        />
                        <LabDemoTippedExpand
                          tipText={pls}
                          multiline={false}
                          className="form-input lab-demo-cell-input"
                          style={{ flex: '1 1 50%', minWidth: 0, fontSize: '0.7rem' }}
                          placeholder="Last"
                          disabled={busy}
                          value={pls}
                          onChange={(e) => setDemoCell(row, 'patientLast', e.target.value)}
                        />
                      </div>
                      {showPatientLine && (
                        <LabDemoThemedTip text={row.patientLine || ''}>
                          <div
                            className="lab-demo-meta"
                            style={{ wordBreak: 'break-word', fontStyle: 'italic', opacity: 0.92, cursor: 'default' }}
                          >
                            {row.patientLine}
                          </div>
                        </LabDemoThemedTip>
                      )}
                    </div>
                  </td>
                  <td style={{ verticalAlign: 'top' }}>
                    <LabDemoTippedExpand
                      tipText={String(demoCell(row, 'dobRaw') ?? '')}
                      multiline={false}
                      className="form-input lab-demo-cell-input"
                      style={{ fontSize: '0.65rem', maxWidth: '5rem', padding: '3px 5px' }}
                      disabled={busy}
                      value={demoCell(row, 'dobRaw')}
                      onChange={(e) => setDemoCell(row, 'dobRaw', e.target.value)}
                    />
                  </td>
                  <td style={{ verticalAlign: 'top' }}>
                    <LabDemoTippedExpand
                      tipText={String(demoCell(row, 'exportDoctor') ?? '')}
                      multiline={false}
                      className="form-input lab-demo-cell-input"
                      style={{ fontSize: '0.7rem' }}
                      placeholder="Provider"
                      disabled={busy}
                      value={demoCell(row, 'exportDoctor')}
                      onChange={(e) => setDemoCell(row, 'exportDoctor', e.target.value)}
                    />
                    {showLisDoc && (
                      <LabDemoThemedTip text={`LIS import (raw): ${row.doctorName || ''}`}>
                        <div className="lab-demo-meta" style={{ cursor: 'default' }}>
                          LIS import: {row.doctorName}
                        </div>
                      </LabDemoThemedTip>
                    )}
                  </td>
                  <td style={{ verticalAlign: 'top' }}>
                    <LabDemoTippedExpand
                      tipText={String(demoCell(row, 'exportMedicalGroup') ?? '')}
                      multiline={false}
                      className="form-input lab-demo-cell-input"
                      style={{ fontSize: '0.7rem' }}
                      disabled={busy}
                      value={demoCell(row, 'exportMedicalGroup')}
                      onChange={(e) => setDemoCell(row, 'exportMedicalGroup', e.target.value)}
                    />
                  </td>
                  <td style={{ verticalAlign: 'top' }}>
                    <LabDemoTippedExpand
                      tipText={String(demoCell(row, 'exportPanel') ?? '')}
                      multiline={false}
                      className="form-input lab-demo-cell-input"
                      style={{ fontSize: '0.7rem' }}
                      disabled={busy}
                      value={demoCell(row, 'exportPanel')}
                      onChange={(e) => setDemoCell(row, 'exportPanel', e.target.value)}
                    />
                  </td>
                  <td style={{ verticalAlign: 'top', minWidth: '6.5rem' }}>
                    <LabDemoThemedTip text={drawTimesHoverText(timeLines)}>
                      {timeLines.length === 0 ? (
                        <span className="lab-demo-meta">—</span>
                      ) : timeLines.length === 1 ? (
                        <div className="lab-demo-meta" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.25 }}>
                          {timeLines[0]}
                        </div>
                      ) : (
                        <div className="lab-demo-meta" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.25 }}>
                          <span>{timeLines[0]}</span>
                          <br />
                          <span>{timeLines[1]}</span>
                        </div>
                      )}
                    </LabDemoThemedTip>
                  </td>
                  <td style={{ verticalAlign: 'top', maxWidth: '12rem' }}>
                    <LabDemoTippedExpand
                      tipText={String(demoCell(row, 'testsOrdered') ?? '')}
                      multiline
                      className="form-textarea lab-demo-cell-input"
                      style={{ fontSize: '0.68rem', minHeight: '36px', width: '100%', wordBreak: 'break-word', padding: '4px 6px' }}
                      disabled={busy}
                      value={demoCell(row, 'testsOrdered')}
                      onChange={(e) => setDemoCell(row, 'testsOrdered', e.target.value)}
                    />
                  </td>
                  <td style={{ verticalAlign: 'top', maxWidth: '9rem' }}>
                    <LabDemoTippedExpand
                      tipText={String(demoCell(row, 'exportDescription') ?? '')}
                      multiline
                      className="form-textarea lab-demo-cell-input"
                      style={{ fontSize: '0.65rem', minHeight: '36px', width: '100%', padding: '4px 6px' }}
                      disabled={busy}
                      placeholder="Notes"
                      value={demoCell(row, 'exportDescription')}
                      onChange={(e) => setDemoCell(row, 'exportDescription', e.target.value)}
                    />
                  </td>
                  <td style={{ verticalAlign: 'top' }}>
                    <LabDemoThemedTip text={String(demoCell(row, 'exportSendOut') ?? '')}>
                      <select
                        className="form-select lab-demo-cell-input"
                        style={{ fontSize: '0.65rem', maxWidth: '100%' }}
                        value={demoCell(row, 'exportSendOut')}
                        disabled={busy}
                        onChange={(e) => setDemoCell(row, 'exportSendOut', e.target.value)}
                      >
                        {mergeSelectOptions(demoUiOptions.sendOut, demoCell(row, 'exportSendOut')).map((opt, i) => (
                          <option key={`send-${i}-${String(opt)}`} value={opt}>
                            {opt || '—'}
                          </option>
                        ))}
                      </select>
                    </LabDemoThemedTip>
                  </td>
                  <td style={{ verticalAlign: 'top' }}>
                    <LabDemoThemedTip text={String(demoCell(row, 'exportResultStatus') ?? '')}>
                      <select
                        className="form-select lab-demo-cell-input"
                        style={{ fontSize: '0.65rem', maxWidth: '100%' }}
                        value={demoCell(row, 'exportResultStatus')}
                        disabled={busy}
                        onChange={(e) => setDemoCell(row, 'exportResultStatus', e.target.value)}
                      >
                        {mergeSelectOptions(demoUiOptions.resultStatus, demoCell(row, 'exportResultStatus')).map(
                          (opt, i) => (
                            <option key={`res-${i}-${String(opt)}`} value={opt}>
                              {opt || '—'}
                            </option>
                          )
                        )}
                      </select>
                    </LabDemoThemedTip>
                  </td>
                  <td style={{ verticalAlign: 'top' }}>
                    <LabDemoThemedTip text={String(demoCell(row, 'exportBillingStatus') ?? '')}>
                      <select
                        className="form-select lab-demo-cell-input"
                        style={{ fontSize: '0.65rem', maxWidth: '100%' }}
                        value={demoCell(row, 'exportBillingStatus')}
                        disabled={busy}
                        onChange={(e) => setDemoCell(row, 'exportBillingStatus', e.target.value)}
                      >
                        {mergeSelectOptions(demoUiOptions.billingStatus, demoCell(row, 'exportBillingStatus')).map(
                          (opt, i) => (
                            <option key={`bill-${i}-${String(opt)}`} value={opt}>
                              {opt || '—'}
                            </option>
                          )
                        )}
                      </select>
                    </LabDemoThemedTip>
                  </td>
                  <td style={{ verticalAlign: 'top' }}>
                    <LabDemoThemedTip text={matchedLabel || '—'}>
                      <select
                        className="form-select lab-demo-cell-input"
                        style={{ fontSize: '0.65rem', maxWidth: '100%' }}
                        value={matchedId}
                        disabled={busy}
                        onChange={(e) => setDemoCell(row, 'matchedClinic', e.target.value || null)}
                      >
                        <option value="">—</option>
                        {clinics.map((c) => (
                          <option key={String(c._id)} value={String(c._id)}>
                            {c.clinicName || c.name}
                          </option>
                        ))}
                      </select>
                    </LabDemoThemedTip>
                  </td>
                </tr>
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      {isFullscreen ? scrollNavButtons : createPortal(scrollNavButtons, document.body)}

      <style>
        {`
          .lab-demo-actions-bar {
            display: flex;
            justify-content: flex-end;
            gap: 8px;
            margin-bottom: 10px;
          }
          .lab-demo-popup-backdrop {
            position: fixed;
            inset: 0;
            background: rgba(0, 0, 0, 0.52);
            z-index: 1200;
            display: grid;
            place-items: center;
            padding: 16px;
          }
          .lab-demo-popup-card {
            width: min(980px, 96vw);
            max-height: min(82vh, 760px);
            overflow: auto;
            background: var(--bg-secondary);
            border: 1px solid var(--border-color);
            border-radius: 10px;
            padding: 14px;
            box-shadow: 0 16px 48px rgba(0, 0, 0, 0.45);
          }
          .lab-demo-popup-head {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 10px;
            margin-bottom: 10px;
          }
          .lab-demo-popup-title { margin: 0; font-size: 0.98rem; }
          .lab-demo-meta { font-size: 0.62rem; color: var(--text-secondary); margin-top: 2px; line-height: 1.3; word-break: break-word; }
          .lab-demo-sheet-table--dense td { vertical-align: top !important; padding: 4px 5px !important; }
          .lab-demo-sheet-table--dense th { padding: 6px 5px !important; font-size: 0.65rem; white-space: nowrap; }
          .lab-demo-sheet-table thead th {
            position: sticky;
            top: 0;
            z-index: 2;
            background: var(--bg-secondary);
            box-shadow: inset 0 -1px 0 var(--border-color);
          }
          .lab-demographics-sheet:fullscreen {
            width: 100vw;
            min-height: 100dvh;
            margin: 0;
            border-radius: 0;
            padding: 10px 12px;
          }
          .lab-demo-sheet-table .form-input, .lab-demo-sheet-table .form-textarea, .lab-demo-sheet-table .form-select { max-width: 100%; }
          .lab-demo-cell-input { padding: 4px 6px !important; min-height: 28px; }
          .lab-demo-float-nav-layer {
            position: fixed;
            inset: 0;
            pointer-events: none;
            z-index: 145;
          }
          .lab-demo-day-nav {
            position: fixed;
            top: 4px;
            left: 50%;
            transform: translateX(-50%);
            z-index: 146;
            pointer-events: auto;
            display: flex;
            align-items: center;
            gap: 6px;
            padding: 4px 8px;
            border-radius: 999px;
            background: color-mix(in srgb, var(--bg-secondary) 92%, #071227);
            border: 1px solid color-mix(in srgb, var(--logo-cyan-teal) 28%, var(--border-color));
            box-shadow: 0 6px 18px rgba(0, 0, 0, 0.28);
            font-size: 0.75rem;
            white-space: nowrap;
          }
          .lab-demo-day-nav-arrow {
            width: 30px;
            min-width: 30px;
            height: 24px;
            padding: 0;
            border-radius: 999px;
            font-size: 0.95rem;
            line-height: 1;
          }
          .lab-demo-day-nav-arrow.is-inactive {
            opacity: 0.5;
            filter: grayscale(0.25);
          }
          .lab-demo-float-nav {
            position: fixed;
            left: 50%;
            transform: translateX(-50%);
            z-index: 146;
            width: 42px;
            min-width: 42px;
            height: 32px;
            padding: 0;
            border-radius: 999px;
            font-size: 1rem;
            line-height: 1;
            opacity: 0.34;
            visibility: visible;
            pointer-events: auto;
            transition: opacity 0.2s ease, box-shadow 0.2s ease, filter 0.2s ease;
            box-shadow: 0 4px 16px rgba(0, 0, 0, 0.22);
          }
          .lab-demo-float-nav:hover {
            opacity: 1;
            box-shadow: 0 10px 22px rgba(0, 0, 0, 0.34);
          }
          .lab-demo-float-nav.is-inactive {
            opacity: 0.22;
            filter: grayscale(0.25);
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.16);
          }
          .lab-demo-float-nav.is-inactive:hover {
            opacity: 0.55;
          }
          .lab-demo-float-nav--top { top: 4px; }
          .lab-demo-float-nav--bottom { bottom: 4px; }
        `}
      </style>
    </div>
  );
};

export default LabDemographicsSheet;
